import { useEffect, useState } from "react";
import { Box, BoxProps, Heading, TimeDisplay } from "@vinomofo/components";
import styled from "styled-components";
import { InlineTimerDisplay } from "../inline-countdown-timer/InlineTimerDisplay";

interface CountdownProps extends BoxProps {
  start: Date;
  end: Date;
  width?: string;
  height?: string;
  countDays?: boolean;
  countHours?: boolean;
  timeFontSize?: string;
  spacing?: string;
  divider?: boolean;
  eventPage?: boolean;
  showHorizontalBar?: boolean;
  isSpirit?: boolean;
  showSecs?: boolean;
  isOfferHeroCard?: boolean;
}

const StyledCountdownTimerBox = styled(Box)`
  z-index: 1;
  opacity: 1 !important;
`;

export const CountdownTimer = ({
  start,
  end,
  width,
  height,
  countDays,
  countHours,
  timeFontSize,
  spacing,
  divider,
  eventPage,
  showHorizontalBar = true,
  isSpirit,
  isOfferHeroCard = false,
  showSecs = false,
  ...rest
}: CountdownProps): JSX.Element => {
  const getTimeState = () => {
    const now = Date.now();
    const startTime = start.getTime();
    const endTime = end.getTime();

    if (now < startTime) return { label: "STARTS IN", target: start };
    if (now < endTime) {
      const label = isSpirit === true ? "DEAL ENDS IN" : "ENDS IN";
      return { label, target: end };
    }
    return { label: eventPage ? "ENDED" : "FINISHED", target: null };
  };

  const initial = getTimeState();
  const [timeLeft, setTimeLeft] = useState(() =>
    initial.target ? calculateTimeLeft(initial.target) : null
  );
  const [label, setLabel] = useState(initial.label);

  useEffect(() => {
    const interval = setInterval(() => {
      const { label: newLabel, target } = getTimeState();

      setLabel(newLabel);
      setTimeLeft(target ? calculateTimeLeft(target) : null);
    }, 1000);

    return () => clearInterval(interval);
  }, [start, end, eventPage]);

  const now = Date.now();
  const isExpired = now > end.getTime();

  const backgroundColor = isExpired
    ? "gray"
    : label === "STARTS IN"
    ? "clockwork"
    : "deal";

  return (
    <StyledCountdownTimerBox
      display="flex"
      flexDirection={isOfferHeroCard ? "column" : "row"}
      {...rest}
      alignItems="center"
    >
      <Box
        textAlign="center"
        backgroundColor={backgroundColor}
        alignSelf="center"
        px="15px"
        borderRadius="5px 5px 0 0"
      >
        <Box display={["block", "none", "block"]}>
          <Heading
            as="span"
            letterSpacing="loose"
            color="white"
            fontSize="10px"
            fontWeight="bold"
          >
            {label}
          </Heading>
        </Box>
      </Box>
      <Box display="flex" alignItems="left">
        <Box
          height={showHorizontalBar ? "1px" : "0px"}
          backgroundColor="divider"
          flexGrow="1"
          marginRight="15px"
        />
        <Box minWidth={["120px", "120px", "120px"]} width={width}>
          {isOfferHeroCard ? (
            <TimeDisplay
              height={height}
              {...timeLeft}
              countDays={timeLeft?.days === 0 ? false : countDays}
              countHours={timeLeft?.hrs === 0 ? false : countHours}
              timeFontSize={timeFontSize}
              spacing={spacing}
              divider={divider}
              showSecs={
                showSecs ||
                (timeLeft?.days === 0 &&
                  timeLeft?.hrs === 0 &&
                  timeLeft?.mins <= 60)
              }
              color={
                label === "ENDS IN" &&
                timeLeft?.days === 0 &&
                timeLeft?.hrs === 0 &&
                timeLeft?.mins <= 60 &&
                "red"
              }
            />
          ) : (
            <InlineTimerDisplay
              isExpandedDisplay={false}
              dateTime={new Date(end).toISOString()}
              size="sm"
              hideDays={timeLeft?.days === 0}
            />
          )}
        </Box>
        <Box
          height={showHorizontalBar ? "1px" : "0px"}
          backgroundColor="divider"
          flexGrow="1"
          marginLeft="15px"
        />
      </Box>
    </StyledCountdownTimerBox>
  );
};

export const calculateTimeLeft = (
  date: Date
): { days: number; hrs: number; mins: number; secs: number } | null => {
  const now = new Date();
  const difference = Math.floor((date.getTime() - now.getTime()) / 1000);
  return breakSecondsByTimeUnits(difference);
};

const breakSecondsByTimeUnits = (
  difference: number
): { days: number; hrs: number; mins: number; secs: number } | null => {
  if (difference <= 0) return null;

  return {
    days: Math.floor(difference / 86400),
    hrs: Math.floor((difference % 86400) / 3600),
    mins: Math.floor((difference % 3600) / 60),
    secs: difference % 60,
  };
};
